import { useCallback, useEffect, useRef, useState } from "react";
import { AxiosInstance } from "axios";
import { API_URL } from "../api/client";
import { PhotoDto } from "../types";

/**
 * Shared photo strip for a plan. The backend proxies both upload and serving
 * (private bucket; each photo is gated by an unguessable view token), so the
 * <img> src is a relative API path resolved against the API origin.
 *
 * `getClient` returns a ready axios instance (re-fetched per request so the
 * organizer's Auth0 token stays fresh). `basePath` is "/api/session" (magic-link
 * member) or "/api/sessions/{id}" (organizer).
 */
const PhotoGallery: React.FC<{ getClient: () => Promise<AxiosInstance>; basePath: string }> = ({
  getClient,
  basePath,
}) => {
  const [photos, setPhotos] = useState<PhotoDto[]>([]);
  const [loaded, setLoaded] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const load = useCallback(async () => {
    try {
      const client = await getClient();
      const { data } = await client.get<PhotoDto[]>(`${basePath}/photos`);
      setPhotos(data);
    } catch {
      // Feature may be disabled server-side; just show nothing.
    } finally {
      setLoaded(true);
    }
  }, [getClient, basePath]);

  useEffect(() => {
    load();
  }, [load]);

  const onPick = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (inputRef.current) inputRef.current.value = ""; // allow re-picking the same file
    if (!file) return;
    setBusy(true);
    setError(null);
    try {
      const form = new FormData();
      form.append("file", file);
      const client = await getClient();
      const { data } = await client.post<PhotoDto>(`${basePath}/photos`, form);
      setPhotos((prev) => [...prev, data]);
    } catch {
      setError("Couldn't add that photo. Try a JPG, PNG, or HEIC under 15MB.");
    } finally {
      setBusy(false);
    }
  };

  if (!loaded) return null;

  return (
    <div className="mt-4 rounded-2xl border border-primary-100 bg-white/70 p-4 text-left">
      <div className="flex items-center justify-between">
        <h3 className="text-sm font-medium text-ink">Photos {photos.length > 0 && `(${photos.length})`}</h3>
        <button
          type="button"
          onClick={() => inputRef.current?.click()}
          disabled={busy}
          className="rounded-xl border border-primary-300 bg-white/70 px-3 py-1.5 text-sm font-medium text-primary-700 transition-colors hover:border-primary-400 disabled:opacity-50"
        >
          {busy ? "Adding…" : "📷 Add photo"}
        </button>
        <input
          ref={inputRef}
          type="file"
          accept="image/*"
          capture="environment"
          onChange={onPick}
          className="hidden"
        />
      </div>
      {error && <p className="mt-2 text-xs text-rose-600">{error}</p>}
      {photos.length === 0 ? (
        <p className="mt-2 text-xs text-ink/55">Share a snapshot from the night — everyone on the plan can see it.</p>
      ) : (
        <div className="mt-3 grid grid-cols-3 gap-2">
          {photos.map((p) => (
            <a key={p.id} href={`${API_URL}${p.url}`} target="_blank" rel="noreferrer" className="block">
              <img
                src={`${API_URL}${p.url}`}
                alt="Shared on the plan"
                loading="lazy"
                className="aspect-square w-full rounded-xl object-cover ring-1 ring-primary-100"
              />
            </a>
          ))}
        </div>
      )}
    </div>
  );
};

export default PhotoGallery;
